import * as _ from 'underscore';

import { AccordionState } from './AccordionState';

export module PermissionsPanelSizeConstants {
  // IMPORTANT: Keep these constants in sync with those in permissions.styl
  // TODO: Refactor to remove this coupling (requires porting to React)
  export const GridRowHeight = 28; // matches $permissionsGridTotalRowHeight
  // The following constants are 2 greater here than they are in the stylesheet, because of the
  // top/bottom border of .tb-explicit-permissions-headers-line.
  export const ExplicitPermissionsHeaderHeightShortText = 228;
  export const ExplicitPermissionsHeaderHeightTallText = 316;

  export const MinimumVisibleRows = 2.5;
  export const MinimumDataGridBodyHeight = GridRowHeight * MinimumVisibleRows;
}

export interface PermissionsPanelElementHeights {
  permissionsPanelHeaderHeight: number;
  permissionsPanelBodyHeight: number;
  explicitPermissionsHeaderHeight: number;
  addRuleRowHeight: number;
  dividerHeight: number;
}


/**
 * This class abstracts the interface between the resizing logic and the DOM
 */
export class PermissionsPanelElementController {

  public preferredExplicitPermissionsDataGridBodyHeight: number;

  private permissionsPanelHeader: JQuery;
  private permissionsPanelBody: JQuery;
  private topPanel: JQuery;
  private explicitPermissionsHeader: JQuery;
  private explicitPermissionsBody: JQuery;
  private addRuleRow: JQuery;
  private bottomPanel: JQuery;
  private divider: JQuery;

  constructor(private permissionsPanel: JQuery) {
    this.findElements();
  }

  public findElements(): void {
    this.permissionsPanelHeader = this.permissionsPanel.find('.tb-permissions-panel-header');
    this.permissionsPanelBody = this.permissionsPanel.find('.tb-permissions-panel-body');
    this.topPanel = this.permissionsPanelBody.find('.tb-permissions-top');
    this.explicitPermissionsHeader = this.topPanel.find('.tb-explicit-permissions-headers-line');
    this.explicitPermissionsBody = this.topPanel.find('.tb-explicit-permissions-body');
    this.addRuleRow = this.topPanel.find('.tb-add-permissions-rule-container');
    this.bottomPanel = this.permissionsPanelBody.find('.tb-permissions-bottom');
    this.divider = this.bottomPanel.find('.tb-permissions-panel-divider');
  }

  public getElementHeights(explicitPermissionsHeaderHeight?: number): PermissionsPanelElementHeights {
    if (!_.isNumber(explicitPermissionsHeaderHeight)) {
      explicitPermissionsHeaderHeight = this.explicitPermissionsHeader.outerHeight() || null;
    }

    return {
      permissionsPanelHeaderHeight: this.permissionsPanelHeader.outerHeight() || null,
      permissionsPanelBodyHeight: this.permissionsPanelBody.outerHeight() || null,
      explicitPermissionsHeaderHeight: explicitPermissionsHeaderHeight,
      addRuleRowHeight: this.addRuleRow.outerHeight() || null,
      dividerHeight: this.divider.outerHeight() || null
    };
  }

  public setTopPanelHeightAndUpdatePreferred(topPanelHeight: number,
                                             sizingLogic?: PermissionsPanelSizingLogic): number {
    if (!sizingLogic) {
      sizingLogic = PermissionsPanelSizingLogic.create(this.getElementHeights());
    }
    const newHeight = sizingLogic.getActualTopPanelHeight(topPanelHeight);

    // These style attributes are usually set by PixelStyleDirectives on those elements, but
    // we must immediately change the style values when setting the top panel height explicitly
    // so that the newly-changed preferred data grid body height can be measured.
    this.topPanel.css('height', newHeight);
    this.bottomPanel.css('top', newHeight);
    // the explicitPermissionsDataGridBody isn't resized until after the next watch, so
    // for now we infer the size it will be instead of measuring it
    this.preferredExplicitPermissionsDataGridBodyHeight =
        newHeight - this.addRuleRow.outerHeight() - this.explicitPermissionsHeader.outerHeight();
    return newHeight;
  }

  public getTopPanelHeightFromPreferredHeight(): number {
    const logic = PermissionsPanelSizingLogic.create(this.getElementHeights());
    const topPanelHeight = logic.computeTopPanelHeight(this.preferredExplicitPermissionsDataGridBodyHeight);
    return logic.getActualTopPanelHeight(topPanelHeight);
  }

  public measureTopPanelHeight() {
    return this.topPanel.outerHeight() || null;
  }

  public measurePermissionsPanelBodyHeight() {
    return this.permissionsPanelBody.outerHeight() || null;
  }
}


/**
 * Logic governing the behavior of the permissions/panel top/bottom panel sizes. Since the element
 * heights may change over time, a new PermissionsPanelSizingLogic object should be created from
 * the current element heights at the beginning of every resize action to ensure that the
 * calculated values are correct.
 */
export class PermissionsPanelSizingLogic {

  private minTopHeight: number;
  private maxTopHeight: number;

  /** Static factory method so construction can be stubbed in unit tests */
  public static create(elementHeights: PermissionsPanelElementHeights): PermissionsPanelSizingLogic {
    return new PermissionsPanelSizingLogic(elementHeights);
  }

  constructor(private elementHeights: PermissionsPanelElementHeights) {
    const panelBodyHeight = this.elementHeights.permissionsPanelBodyHeight;
    const minBottomHeight = this.computeBottomPanelHeight(PermissionsPanelSizeConstants.MinimumDataGridBodyHeight);
    this.minTopHeight = this.computeTopPanelHeight(PermissionsPanelSizeConstants.MinimumDataGridBodyHeight);
    this.maxTopHeight = panelBodyHeight - minBottomHeight;
  }

  public computeTopPanelHeight(explicitPermissionsBodyHeight: number): number {
    return this.elementHeights.explicitPermissionsHeaderHeight +
        explicitPermissionsBodyHeight + this.elementHeights.addRuleRowHeight;
  }

  private computeBottomPanelHeight(effectivePermissionsContainerHeight: number): number {
    const dividerHeight = this.elementHeights.dividerHeight;

    // Use the top header as a surrogate to measure the bottom header, because they are the same
    // height but the bottom header isn't always shown, so its height may measure as 0 px.
    const bottomHeaderHeight = this.elementHeights.permissionsPanelHeaderHeight;

    return dividerHeight + bottomHeaderHeight + effectivePermissionsContainerHeight;
  }

  public getActualTopPanelHeight(desiredPanelHeight: number): number {
    const newHeight = Math.max(Math.min(desiredPanelHeight,
      this.maxTopPanelHeight()), this.minTopPanelHeight());
    return newHeight;
  }

  public minTopPanelHeight(): number {
    return this.minTopHeight;
  }

  public maxTopPanelHeight(): number {
    return this.maxTopHeight;
  }
}


/**
 * Controls the resizing of the two panes of the permissions panel when the user drags the divider
 */
export class PermissionsPanelResizer {

  private sizingLogic: PermissionsPanelSizingLogic;
  private startY: number;
  private startTopHeight: number;

  constructor(private elementCtrl: PermissionsPanelElementController) {}

  /**
   * Starts a resize action for the divider between the top and the bottom panels
   * @param yPos The initial vertical position of the mousedown event on the divider
   */
  public startResize(yPos: number): void {
    this.sizingLogic = PermissionsPanelSizingLogic.create(this.elementCtrl.getElementHeights());
    this.startTopHeight = this.elementCtrl.measureTopPanelHeight();
    this.startY = yPos;
  }

  /**
   * Continually resizes the top and bottom panels as the divider is moved
   * @param yPos The vertical position of the cursor as the divider is dragged
   */
  public continueResize(yPos: number): void {
    const dy = yPos - this.startY;
    const newTopHeight = dy + this.startTopHeight;
    this.elementCtrl.setTopPanelHeightAndUpdatePreferred(newTopHeight, this.sizingLogic);
  }
}


/**
 * Controls the open/close behavior of the header text, as well as its associated resizing of the
 * top/bottom panes of the permissions panel
 */
export class PermissionsHeaderTextAccordion implements AccordionState {

  private headerOpen: boolean = false;

  constructor(private elementCtrl: PermissionsPanelElementController,
              private shortHeaderTextFn: () => boolean) {}

  public isEnabled(): boolean {
    return this.headerOpen || this.willOpenHeaderFit();
  }

  public isSectionOpen(): boolean {
    if (this.headerOpen && !this.willOpenHeaderFit()) {
      this.headerOpen = false;
    }
    return this.headerOpen;
  }

  public toggleSection() {
    if (this.isEnabled()) {
      this.headerOpen = !this.headerOpen;
    }
  }

  private willOpenHeaderFit(): boolean {
    const newHeaderHeight = this.shortHeaderTextFn()
        ? PermissionsPanelSizeConstants.ExplicitPermissionsHeaderHeightShortText
        : PermissionsPanelSizeConstants.ExplicitPermissionsHeaderHeightTallText;
    const logic = PermissionsPanelSizingLogic.create(this.elementCtrl.getElementHeights(newHeaderHeight));
    return logic.minTopPanelHeight() <= logic.maxTopPanelHeight();
  }
}
